import { type FC } from "react";

import { type TypeInfo } from "@moment/api-collab/differential/schema/index";

import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../..";
import { Button } from "../../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../../Popover";
import { type BaseViewHandle } from "../BaseViewHandle";
import ColumnItemEndAddon from "../ColumnItemEndAddon";
import { ColumnTypeIcon } from "../ColumnTypeIcon";
import ColumnDetails from "./ColumnDetails";
import ColumnsPopoverContent from "./ColumnsPopoverContent";
import { useColumnsPopoverStore } from "./useColumnsPopoverStore";
import { useRenameColumnState } from "./useRenameColumnState";

const ColumnsPopover: FC<{ viewHandle: BaseViewHandle }> = ({ viewHandle }) => {
	const { popoverOpen, setPopoverOpen, toggleEditColumnPopover } = useColumnsPopoverStore(
		viewHandle.uiInstanceKey
	);

	const column =
		typeof popoverOpen === "number" ? viewHandle.columns.allColumns[popoverOpen] : undefined;
	const columnDef = column?.columnDef;
	const displayOptions = column?.displayOptions;
	const type = columnDef?.typeInfo?.enrichedType ?? "Text";
	const typeInfo = columnDef?.typeInfo;

	const {
		currentHeaderValue,
		internalHeaderValue,
		setInternalHeaderValue,
		headerChanged,
		newColumnName,
	} = useRenameColumnState(viewHandle, column);

	return (
		<Popover
			open={popoverOpen !== undefined}
			onOpenChange={(open) => setPopoverOpen(open ? "columns" : undefined)}
		>
			<PopoverTrigger asChild>
				<Button preset="quaternary" size="md" className="!px-1.5">
					Columns
				</Button>
			</PopoverTrigger>
			<PopoverContent className="!w-96">
				{popoverOpen === "columns" && (
					<ColumnsPopoverContent
						viewHandle={viewHandle}
						toggleEditColumnPopover={toggleEditColumnPopover}
					/>
				)}
				{columnDef && (
					<div className="flex flex-col gap-2">
						<div>
							<Input
								// `PopoverContent` is focus-trapped so this is ok.
								//

								autoFocus
								value={internalHeaderValue}
								elevation="raised"
								endAddon={<ColumnItemEndAddon displayOptions={displayOptions} />}
								onChange={(e) => setInternalHeaderValue(e.target.value)}
								onBlur={() => {
									if (!newColumnName) {
										return;
									}
									viewHandle.renameColumn(
										columnDef.name,
										internalHeaderValue ?? "",
										newColumnName
									);
								}}
							/>
							{headerChanged && (
								<div className="text-sm text-secondary my-1">
									⇢ and migrate data from{" "}
									<code className="font-medium">.{currentHeaderValue}</code> to{" "}
									<code className="font-medium">.{newColumnName}</code>
								</div>
							)}
						</div>
						<Select
							value={type}
							onValueChange={(value: TypeInfo["enrichedType"]) => {
								if (!columnDef) {
									return;
								}
								switch (value) {
									case "Text":
										viewHandle.updateColumnType(columnDef.name, typeInfo, {
											...typeInfo,
											enrichedType: "Text",
										});
										break;
									case "Select":
										viewHandle.updateColumnType(columnDef.name, typeInfo, {
											...typeInfo,
											enrichedType: "Select",
											options: [],
										});
										break;
									case "Reaction":
										viewHandle.updateColumnType(columnDef.name, typeInfo, {
											...typeInfo,
											enrichedType: "Reaction",
										});
										break;
									case "Person":
										viewHandle.updateColumnType(columnDef.name, typeInfo, {
											...typeInfo,
											enrichedType: "Person",
											defaultValue: "Empty",
										});
										break;
									default: {
										const _: never = value;
										throw new Error(`Unknown column type: ${_}`);
									}
								}
							}}
						>
							<SelectTrigger elevation="raised">
								<ColumnTypeIcon type={type} />
								<SelectValue placeholder="Select a type" className="flex gap-2">
									{type}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Text">Text</SelectItem>
								<SelectItem value="Reaction">Reaction</SelectItem>
								<SelectItem value="Select">Select List</SelectItem>
								<SelectItem value="Person">Person</SelectItem>
							</SelectContent>
						</Select>
						<ColumnDetails column={column} viewHandle={viewHandle} />
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
};

export default ColumnsPopover;
